import { MappingFeature } from '@/entities/dashboard';

/**
 * Typed version of `Object.entries`.
 *
 *
 * @template T - Object type extending Record<string, unknown>
 * @param obj - The object to extract entries from
 * @returns Array of key-value tuples preserving original key and value types
 *
 * @example
 * const counts = {
 *   Warehouse: 3,
 *   Port: 1,
 *   Customer: 5,
 * } as const;
 *
 * const entries = typedEntries(counts);
 * // Type:
 * // Array<["Warehouse" | "Port" | "Customer", number]>
 *
 * entries.map(([key, value]) => {
 *   // key is strongly typed (not just string)
 *   console.log(key, value);
 * });
 *
 * @example
 * type StatusMap = Record<"active" | "inactive", boolean>;
 *
 * const status: StatusMap = {
 *   active: true,
 *   inactive: false,
 * };
 *
 * typedEntries(status).forEach(([key, value]) => {
 *   // key: "active" | "inactive"
 *   // value: boolean
 * });
 */
export function typedEntries<T extends Record<string, unknown>>(obj: T): Array<[keyof T & string, T[keyof T]]> {
  return Object.entries(obj) as Array<[keyof T & string, T[keyof T]]>;
}

/**
 * Extract data value from location data
 * Handles both current and historical (time machine) data
 */
export function extractDataValue(
  data: MappingFeature | undefined,
  showTimeMachine: boolean,
  weekNo: number | undefined
): number {
  if (!data) return 0;

  // Use totalValue if time machine is off or no progress data
  const shouldUseProgress = showTimeMachine && data.progress?.length > 0 && weekNo !== undefined;

  if (!shouldUseProgress) {
    return Math.abs(data.totalValue || 0);
  }

  // Calculate historical value from progress array
  const targetIndex = data.progress.length - weekNo - 1;
  const isValidIndex = targetIndex >= 0 && targetIndex < data.progress.length;

  const value = isValidIndex ? data.progress[targetIndex] : data.totalValue || 0;

  return Math.abs(value);
}
